import { useState } from "react";
import { useDispatch } from "react-redux";
import ReactGA from "react-ga4";
import {
  useVerificationMutation,
  useRegisterMutation,
  useLoginMutation,
} from "../../store/auth/usersApiSlice";
import { setToken, setUserInfo } from "../../store/auth/authSlice";
import { validateEmail } from "../../utils/Helper";

/* Registration behaviour for the first three sign-up steps, lifted out of components/public/SignUp/{Email,Verification,Password}.jsx so a redesigned screen... */

/* Step 1 — work email, requests the 6-digit code. */
export const useSignUpEmail = ({ onNext = () => {} } = {}) => {
  const [error, setError] = useState("");
  const [verify, { isLoading }] = useVerificationMutation();

  const submit = async (email) => {
    ReactGA.event({
      category: "Create New Account",
      action: "Create New Account",
      label: `Clicked on Verify Email; ${String(email || "").replace(/@/g, "=")}`,
    });
    try {
      if (!validateEmail(email)) {
        setError("Invalid Email, Please enter a valid email");
        return;
      }
      setError("");
      const res = await verify({ email }).unwrap();

      if (res?.status) {
        if (res?.status === "OTP_SENT") {
          onNext(email, res?.encrypted);
        } else {
          setError(res?.message);
        }
      } else {
        setError("Something Went Wrong");
      }
    } catch (err) {
      setError(
        err?.data ? Object.values(err?.data)[0] : "An error occurred. Please try again later."
      );
    }
  };

  return { submit, error, setError, isLoading };
};

/* Step 2 — verify the code against the encrypted challenge from step 1. */
export const useSignUpVerification = ({
  email,
  verificationCode,
  onNext = () => {},
  onResent = () => {},
} = {}) => {
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [verify, { isLoading }] = useVerificationMutation();

  const resend = async () => {
    ReactGA.event({
      category: "Create New Account",
      action: "Create New Account",
      label: `Clicked on Resend Code; ${String(email || "").replace(/@/g, "=")}`,
    });
    try {
      setError("");
      setResent(false);
      setResending(true);
      const res = await verify({ email }).unwrap();

      if (res?.status === "OTP_SENT") {
        // The new challenge replaces the old one; the previous code is dead.
        onResent(res?.encrypted);
        setExpired(false);
        setResent(true);
      } else {
        setError(res?.message || "Something Went Wrong");
      }
    } catch (err) {
      setError(
        err?.data ? Object.values(err?.data)[0] : "An error occurred. Please try again later."
      );
    } finally {
      setResending(false);
    }
  };

  const submit = async (otp) => {
    ReactGA.event({
      category: "Create New Account",
      action: "Submitted OTP for login",
      label: `user Doing verification using OTP; ${String(email || "").replace(/@/g, "=")}`,
    });
    // Transcribed: the code is sent as a Number, not the digit string.
    const otpNumber = Number(String(otp || "").split("").join(""));

    try {
      setError("");
      const res = await verify({
        email,
        encrypted: verificationCode,
        code: otpNumber,
      }).unwrap();

      if (res?.status) {
        if (res?.status === "OTP_VALIDATED") {
          // The register call in step 3 reads this back out.
          sessionStorage.setItem("temp_token", res.token);
          setExpired(false);
          onNext();
        } else {
          if (res?.status === "OTP_EXPIRED") setExpired(true);
          setError(res?.message || "Wrong OTP Please Try Again");
        }
      } else {
        setError("Something Went Wrong");
      }
    } catch (err) {
      setError(
        err?.data ? Object.values(err?.data)[0] : "An error occurred. Please try again later."
      );
    }
  };

  return {
    submit,
    resend,
    error,
    setError,
    expired,
    resending,
    resent,
    // The mutation is shared with `resend`, so a resend must not make the
    // Verify button read "Verifying…".
    isLoading: isLoading && !resending,
  };
};

/* Step 3 — profile + password: register, then sign the new account in. */
export const useSignUpProfile = ({
  email,
  isArticle = false,
  slug = null,
  onNext = () => {},
} = {}) => {
  const dispatch = useDispatch();
  const [error, setError] = useState("");
  const [register, { isLoading }] = useRegisterMutation();
  const [login] = useLoginMutation();

  const submit = async (values) => {
    const { name, designation, employer_company_name, phone, password } = values;

    ReactGA.event({
      category: "Create New Account",
      action: `Adding Company Profile; ${String(email || "").replace(/@/g, "=")}`,
      label: "User Submitted data for Created his Profile Profile Note Created Yet.",
    });

    const lastVisited = localStorage.getItem("lastVisited");
    const userToken = sessionStorage.getItem("temp_token");
    const inviteCode = localStorage.getItem("invite_code");
    const splittedData = lastVisited ? lastVisited?.split("/") : [];

    try {
      setError("");
      const res = await register({
        token: userToken,
        name,
        email,
        password,
        designation,
        employer_company_name,
        mobile_number: phone,
        ...(inviteCode && { invite_code: inviteCode }),
        ...(splittedData?.[1] === "search" && { referance: splittedData?.[2] }),
      }).unwrap();

      dispatch(setUserInfo({ ...res }));

      const resLogin = await login({
        email,
        password,
        ...(isArticle && { return_magic_token: true }),
        ...(inviteCode && { invite_code: inviteCode }),
      }).unwrap();

      let token = resLogin?.token || resLogin?.access;
      dispatch(setToken(token));
      if (token) {
        ReactGA.event({
          category: "Create New Account",
          action: "Creating Profile",
          label: "User Created his Profile by setting password",
        });
        if (isArticle) {
          const baseUrl = `${process.env.REACT_APP_K_HUB_URL}/magic/${resLogin.magic_token}`;
          // Only append slug if it's available
          const url = slug ? `${baseUrl}?slug=${slug}` : baseUrl;
          window.open(url, "_blank");
        }
        onNext(true);
      }
    } catch (err) {
      setError(
        err?.data ? Object.values(err?.data)[0] : "An error occurred. Please try again later."
      );
    }
  };

  return { submit, error, setError, isLoading };
};
